import { reactive, shallowRef } from 'vue';
import type { Id } from '@vp/shared-types';
import type { Editor, Renderer, RendererStats } from '@vp/render';
import type { CanvasSession } from '../session';

/** Reactive view of the active editor for Vue components. */
export const ui = reactive({
  selection: [] as Id[],
  /** Object under the pointer. */
  hover: null as Id | null,
  tool: 'select',
  canUndo: false,
  canRedo: false,
  /** A camera jump can be undone / redone (Alt+Left / Alt+Right). */
  canViewBack: false,
  canViewForward: false,
  zoom: 1,
  /** Bumped (once a frame at most) when the camera moves: overlays placed on objects follow. */
  camRev: 0,
  /** Bumped (throttled) on every document change so panels recompute. */
  docRev: 0,
  editingText: null as Id | null,
  /** Bezier path whose points are being edited. */
  pathEditing: null as Id | null,
  readOnly: true,
  backend: 'webgl2' as 'webgl2' | 'canvas2d',
  toolsRev: 0,
  /** Mode C object whose live view currently receives pointer input. */
  activeView: null as Id | null,
  /** A pointer gesture (move/resize/marquee…) is in progress — contextual UI hides. */
  dragging: false,
  searchOpen: false,
  keysOpen: false,
  /** Command palette (Ctrl+K). */
  paletteOpen: false,
  /** Object whose notes (comments and reactions) are open. */
  notesFor: null as Id | null,
  /** Saved views card. */
  bookmarksOpen: false,
  /** Object whose link is being set. */
  linkFor: null as Id | null,
  /** DSkhema code window: an existing diagram (id) or a new one (id null, placed at the view center). */
  diagramEdit: null as { id: Id | null; source: string } | null,
  /** The properties bar moved below the top pills (not enough room between them). */
  barBelow: false,
  /** Side chat (AI, team, personal). */
  chatOpen: false,
  /** Zen: the canvas stays usable, the floating panels are hidden. */
  zen: false,
  /** A module panel open in the right sidebar (object settings there follow `settings.propsDocked`). */
  sidebar: null as { kind: 'panel'; moduleId: string } | null,
});

export const stats = shallowRef<RendererStats | null>(null);

/** Non-reactive handles to the live engine objects. */
export const live: { editor: Editor | null; renderer: Renderer | null; session: CanvasSession | null } = {
  editor: null,
  renderer: null,
  session: null,
};

// Inspection hook for e2e / performance tests (page-local only; module iframes have no access).
(globalThis as unknown as { __vp: unknown }).__vp = { live, ui, stats };

let revScheduled = false;
export function bumpDocRev(): void {
  if (revScheduled) return;
  revScheduled = true;
  requestAnimationFrame(() => {
    revScheduled = false;
    ui.docRev++;
  });
}

export function syncHistory(): void {
  const ed = live.editor;
  ui.canUndo = !!ed?.history.canUndo;
  ui.canRedo = !!ed?.history.canRedo;
}

export function syncViews(): void {
  const ed = live.editor;
  ui.canViewBack = !!ed?.canViewBack;
  ui.canViewForward = !!ed?.canViewForward;
}
